// UI
import { Alert, Button, Col, Flex, Input, Popover, Row, Switch } from 'antd';
import { FormInstance } from 'antd/es/form';
import default1 from '../../../src/studio/assets/resource_default_1.png';

// Globals
import { API, graphqlOperation } from 'aws-amplify';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';

// Locals
import {
  addCardProps,
  setIsInitialScrapingDone,
  setIsScrapingUrl,
  updateCardProps,
  updateEditingCardProps,
} from '../../actions';

// Types
import { FeedCard, IState } from '../../graphql/types';
import {
  defaultImageEditorProps,
  handleInputChange,
  isUrlHttps,
  urlRegex,
  urlWithoutHttps,
} from '../utils';
import styles from './CardForm.module.css';
import { CardFormField } from './CardFormField';
// import CardFormTextArea from './CardFormTextArea';
import ImageEditor from '../ImageEditor/ImageEditor';
import useFeedCardForm from '../../hooks/useFeedCardForm';
import { ImageEditorProps } from '../../types';
import { CardType, GetUrlMetaDataQuery, ResourceType } from '../../graphql/api';
import deleteIcon from '../../studio/assets/feedcardIcons/deleteIcon.png';
import { getUrlMetaData } from '../../graphql/queries';

const processUrl = (value: string): string => {
  if (value.includes('youtu')) {
    if (value.includes('youtu.be')) {
      const videoIdMatch = value.match(/youtu\.be\/(.{11})/);
      if (videoIdMatch && videoIdMatch[1]) {
        value = `https://www.youtube.com/watch?v=${videoIdMatch[1]}`;
      }
    }
  }

  const matched = value.match(/^(https?:\/\/)?(.+)$/);
  const prefix = matched ? (matched[1] ? matched[1] : 'https://') : '';
  const newUrl = matched ? matched[2] : value;
  return prefix + newUrl;
};

const ResourceForm: React.FC<{
  form: FormInstance;
  saving: boolean;
  editorRef;
  setEditorRef;
  setFormType: (type: CardType) => void;
}> = ({ form, saving, setFormType, editorRef, setEditorRef }) => {
  const dispatch = useDispatch();

  const [customImageCache, setCustomImageCache] = useState({
    uploadedImgBlob: '',
    customImage: '',
    imageBlob: '',
  });

  const isScraping = useSelector<IState, boolean>(state => !!state?.isScraping);

  const [lastFetchedURL, setLastFetchedURL] = useState('');

  const cardProps = useSelector<IState, Partial<FeedCard> | undefined>(
    state => state.cardProps
  );

  const editingCardProps = useSelector<IState, Partial<FeedCard> | undefined>(
    state => state.editingCardProps
  );

  const [metaDataMissing, setMetaDataMissing] = useState<boolean | undefined>(
    !(cardProps?.source || cardProps?.title) ? undefined : false
  );

  const isScraped = useSelector<IState, boolean>(
    state => !!(state.isInitialScrapingDone || editingCardProps)
  );

  const [videoCardEnabled, setVideoCardEnabled] = useState<boolean>(false);

  const { handleRemove, updateImageCardProps } = useFeedCardForm(
    cardProps?.type
  );

  const newImgLoadedBlob = useSelector<IState, string | undefined>(
    state => state.newImgLoadedBlob
  );

  useEffect(() => {
    if (cardProps?.customImage && cardProps?.customImage.length > 0) {
      if (customImageCache.customImage !== cardProps?.customImage) {
        setCustomImageCache({
          customImage: cardProps.customImage,
          uploadedImgBlob: cardProps.uploadedImgBlob || '',
          imageBlob: cardProps.imageBlob || '',
        });
      }
    }

    // revert from cache so user doesn't lose image if flicks switch
    if (
      !!cardProps?.customImageName &&
      customImageCache.customImage.length > 0
    ) {
      if (cardProps?.customImage !== customImageCache.customImage) {
        dispatch(addCardProps('customImage', customImageCache.customImage));
      }
      if (cardProps?.uploadedImgBlob !== customImageCache.uploadedImgBlob) {
        dispatch(
          addCardProps('uploadedImgBlob', customImageCache.uploadedImgBlob)
        );
      }
      if (cardProps?.imageBlob !== customImageCache.imageBlob) {
        dispatch(addCardProps('imageBlob', customImageCache.imageBlob));
      }
    }
  }, [
    cardProps?.customImage,
    cardProps?.imageBlob,
    cardProps?.uploadedImgBlob,
    cardProps?.customImageName,
    customImageCache,
    dispatch,
  ]);

  useEffect(() => {
    if (cardProps) {
      // Workaround as per https://stackoverflow.com/questions/60471705/antd-form-setfieldvalue-on-onchange-event-is-not-working
      setTimeout(() => {
        form.setFieldsValue({
          resourceUrl: (cardProps.url || '').replace(/^https:\/\//, ''),
          resourceSource: cardProps.source,
          resourceTitle: cardProps.title,
        });
      }, 100);
    }
  }, [cardProps, form]);

  const isValidYoutubeOrVimeo = useCallback(async () => {
    let youtubeValid = false;
    let vimeoValid = false;

    if (videoCardEnabled) {
      setVideoCardEnabled(false);
    }

    if (cardProps?.url) {
      if (cardProps?.url.includes('vimeo')) {
        try {
          const vimeoRes = await fetch(
            `https://vimeo.com/api/oembed.json?url=${cardProps?.url}`
          );
          if (vimeoRes.ok) vimeoValid = true;
        } catch (e) {}
      }

      if (cardProps?.url.includes('youtu')) {
        try {
          const youtubeRes = await fetch(
            `https://www.youtube.com/oembed?format=json&url=${cardProps?.url}`
          );
          if (youtubeRes.ok) youtubeValid = true;
        } catch (e) {}
      }
    }

    return youtubeValid || vimeoValid;
  }, [cardProps, videoCardEnabled]);

  React.useEffect(() => {
    if (cardProps?.url && !videoCardEnabled) {
      isValidYoutubeOrVimeo()
        .then(isValid => {
          if (isValid) {
            setVideoCardEnabled(true);
          }
        })
        .catch(e => console.log(e));
    }
  }, [cardProps, videoCardEnabled, isValidYoutubeOrVimeo]);

  const handleUrlSubmit = useCallback(async () => {
    form.validateFields(['resourceUrl']);

    if (await isValidYoutubeOrVimeo()) {
      setVideoCardEnabled(true);
    }

    if (
      !isScraping &&
      cardProps?.url &&
      urlRegex.test(urlWithoutHttps(cardProps.url) ?? '') &&
      cardProps.url !== cardProps?.fetchedUrl
    ) {
      setMetaDataMissing(false);
      dispatch(setIsScrapingUrl(true));
      try {
        const res = await (API.graphql(
          graphqlOperation(getUrlMetaData, {
            url: cardProps.url.replace(/^https:\/\//, ''),
          })
        ) as Promise<{ data: GetUrlMetaDataQuery }>);

        // Workaround as per https://stackoverflow.com/questions/60471705/antd-form-setfieldvalue-on-onchange-event-is-not-working
        setTimeout(() => {
          form.setFieldsValue({
            resourceUrl: (cardProps.url || '').replace(/^https:\/\//, ''),
            resourceSource: res.data.getUrlMetaData?.publisher,
            resourceTitle: res.data.getUrlMetaData?.title,
          });
        }, 100);
        cardProps?.id
          ? dispatch(
              updateEditingCardProps({
                title: res.data.getUrlMetaData?.title,
                source: res.data.getUrlMetaData?.publisher,
                fetchedUrl: cardProps.url,
                imageUrl: isUrlHttps(res.data.getUrlMetaData?.image)
                  ? res.data.getUrlMetaData?.image
                  : null,
                iconUrl: isUrlHttps(res.data.getUrlMetaData?.logo)
                  ? res.data.getUrlMetaData?.logo
                  : null,
              })
            )
          : dispatch(
              updateCardProps({
                title: res.data.getUrlMetaData?.title,
                source: res.data.getUrlMetaData?.publisher,
                fetchedUrl: cardProps.url,
                imageUrl: isUrlHttps(res.data.getUrlMetaData?.image)
                  ? res.data.getUrlMetaData?.image
                  : null,
                iconUrl: isUrlHttps(res.data.getUrlMetaData?.logo)
                  ? res.data.getUrlMetaData?.logo
                  : null,
              })
            );
        if (
          !res.data.getUrlMetaData?.title ||
          !res.data.getUrlMetaData?.publisher
        ) {
          setMetaDataMissing(true);
        }
      } catch (e) {
        setMetaDataMissing(true);
      } finally {
        batch(() => {
          dispatch(setIsScrapingUrl(false));
          dispatch(setIsInitialScrapingDone());
        });

        setLastFetchedURL(cardProps.url);
      }
    }
  }, [dispatch, form, cardProps, isScraping, isValidYoutubeOrVimeo]);

  const urlValidationTooltip = title => (
    <Popover
      placement="topLeft"
      open={!!cardProps}
      title={title}
      overlayClassName="urlValidationTooltip"
      align={{ offset: [140, -30] }}
    />
  );

  const imageUrl = useMemo(() => {
    if (cardProps?.customImage) {
      if (cardProps?.uploadedImgBlob as string) {
        return cardProps?.imageBlob as string;
      } else if (cardProps?.imageBlob) {
        return cardProps?.imageBlob;
      }
    }

    return null;
  }, [
    cardProps?.customImage,
    cardProps?.uploadedImgBlob,
    cardProps?.imageBlob,
  ]);

  const handleLoadImage = useCallback(
    file => {
      updateImageCardProps(file, true);
    },
    [updateImageCardProps]
  );

  const imageEditorProps = useMemo<ImageEditorProps>(
    () => ({
      ...defaultImageEditorProps,
      image: imageUrl,
      fileName: cardProps?.customImageName || 'unnamed_file',
      handleRemove,
      updateImageCardProps,
      handleLoadImage,
      editorRef,
      setEditorRef,
      disabled: saving,
      hideUploadIcon: true,
      imgWidth: 400,
      imgHeight: 227,
      imgBorderWidth: 60,
      imgBorderHeight: 60,
      staticImageClass: styles.staticImageRectangle,
      buttons: (
        <div className={styles.fcIcons}>
          <div
            onClick={() => {
              handleRemove();
              dispatch(addCardProps('customImageName', null));
              setCustomImageCache({
                uploadedImgBlob: '',
                customImage: '',
                imageBlob: '',
              });
            }}
            className={styles.deleteIconWrapper}
          >
            <img
              className={styles.deleteIcon}
              src={deleteIcon}
              alt="delete icon"
            />
          </div>
        </div>
      ),
      text: (
        <div>
          <div>
            <span className={styles.imgEditorUnderlineText}>
              Upload an image
            </span>{' '}
            or drag it here
          </div>
          <div>
            <span className={styles.imgEditorMutedText}>
              PNGs, JPGs, or GIFs up to 10MB
            </span>
          </div>
        </div>
      ),
    }),
    [
      cardProps?.customImageName,
      handleLoadImage,
      imageUrl,
      setEditorRef,
      updateImageCardProps,
      editorRef,
      handleRemove,
      saving,
      dispatch,
    ]
  );

  return (
    <div className={styles.formWrapper}>
      <div>
        <Col span={24}>
          <CardFormField
            name="resourceUrl"
            boldLabel={true}
            fieldLabel="URL"
            customValidator={e => {
              const matched = e.target.value.match(/^(https:\/\/)(.+)$/);
              return matched?.length > 2 ? matched[2] : e.target.value;
            }}
            customRules={[
              {
                message: urlValidationTooltip('Only https address is allowed'),
                pattern: /^(?!http:\/\/)(.+)$/,
                validateTrigger: 'onBlur',
              },
              {
                message: urlValidationTooltip('Incorrect url format'),
                pattern: urlRegex,
                validateTrigger: 'onBlur',
              },
            ]}
            className={`${metaDataMissing ? styles.noBottomMargin : ''}`}
          >
            <Row gutter={15}>
              <Flex style={{ width: 'calc(100% - 8px)' }} gap={'middle'}>
                <Input
                  autoFocus={!newImgLoadedBlob}
                  className="resource-card-url-input"
                  value={cardProps?.url ? urlWithoutHttps(cardProps?.url) : ''}
                  disabled={isScraping || saving}
                  onChange={handleInputChange(value => {
                    dispatch(addCardProps('url', processUrl(value)));
                    if (videoCardEnabled) {
                      setVideoCardEnabled(false);
                      dispatch(
                        updateCardProps({ resourceType: ResourceType.website })
                      );
                    }
                  })}
                  placeholder="www.linkofyourchoice.com"
                />

                <Button
                  type={'primary'}
                  onClick={handleUrlSubmit}
                  loading={isScraping || saving}
                  disabled={lastFetchedURL === cardProps?.url}
                >
                  {lastFetchedURL === '' ? 'Next' : 'Change'}
                </Button>
              </Flex>
            </Row>
          </CardFormField>
        </Col>
        {!isScraping && metaDataMissing && (
          <Col>
            <Alert
              className={styles.metaDataMissing}
              message="Oh no! We weren't able to grab the metadata from this link, so you'll have to fill in some blanks."
              type="warning"
            />
          </Col>
        )}
        {!isScraping && videoCardEnabled && (
          <Col span={24} style={{ display: 'flex', placeContent: 'center' }}>
            <div className={styles.videoCardToggle}>
              This looks like a video link. If you'd rather display it as a
              video card,
              <button
                onClick={() => {
                  dispatch(updateCardProps({ type: CardType.video }));
                  setFormType(CardType.video);
                }}
              >
                click here
              </button>
              .
            </div>
          </Col>
        )}
        {isScraped && !isScraping && (
          <>
            {!!cardProps?.customImageName ? (
              cardProps?.resourceType !== ResourceType.video && (
                <>
                  <Col
                    span={24}
                    style={{
                      display: 'flex',
                      placeContent: 'center',
                      margin: '16px auto',
                    }}
                    className={
                      metaDataMissing
                        ? styles.defaultImgEditorBoxSizeNoMeta
                        : styles.defaultImgEditorBoxSize
                    }
                  >
                    <ImageEditor props={imageEditorProps} />
                  </Col>
                </>
              )
            ) : (
              <>
                <Col
                  span={24}
                  style={{ display: 'flex', placeContent: 'center' }}
                >
                  <div
                    className={
                      metaDataMissing
                        ? styles.defaultImgEditorBoxSizeNoMeta
                        : styles.defaultImgEditorBoxSize
                    }
                    style={{ display: 'flex' }}
                  >
                    <img
                      src={cardProps?.imageUrl || default1}
                      alt=""
                      className={cardProps?.imageUrl ? '' : 'placeholder'}
                      style={{
                        maxWidth: '534px',
                        borderRadius: '8pt',
                        backgroundColor: 'transparent',
                        objectFit: cardProps?.imageUrl ? 'contain' : 'fill',
                      }}
                    />
                  </div>
                </Col>
              </>
            )}
            <Col span={24} className={styles.useCustomImage}>
              <Switch
                checkedChildren="On"
                unCheckedChildren="Off"
                className={`${
                  !!cardProps?.customImageName
                    ? 'switchEnabled'
                    : 'switchDisabled'
                }`}
                onChange={e => {
                  dispatch(
                    addCardProps(
                      'customImageName',
                      e.valueOf() ? 'unnamed_file' : null
                    )
                  );
                  dispatch(addCardProps('customImage', null));
                }}
                checked={!!cardProps?.customImageName}
              />
              Upload custom image
            </Col>
            <Col span={24}>
              <Flex vertical={true} style={{ marginTop: '16px' }}>
                <CardFormField
                  name="resourceTitle"
                  boldLabel={true}
                  fieldLabel="Title"
                  errorName={'a title'}
                >
                  <Input
                    value={cardProps?.title ?? ''}
                    placeholder={
                      isScraping ? 'fetching meta data' : 'Title of article'
                    }
                    className="modal-form-input"
                    onChange={handleInputChange(value =>
                      dispatch(addCardProps('title', value))
                    )}
                    disabled={
                      isScraping ||
                      typeof metaDataMissing === 'undefined' ||
                      saving
                    }
                  />
                </CardFormField>
                <CardFormField
                  name="resourceSource"
                  boldLabel={true}
                  fieldLabel="Source"
                  errorName={'the source'}
                >
                  <Input
                    className="modal-form-input"
                    value={cardProps?.source ?? ''}
                    disabled={
                      isScraping ||
                      typeof metaDataMissing === 'undefined' ||
                      saving
                    }
                    placeholder={
                      isScraping
                        ? 'fetching meta data'
                        : 'Title of website it comes from'
                    }
                    onChange={handleInputChange(value =>
                      dispatch(addCardProps('source', value))
                    )}
                  />
                </CardFormField>
              </Flex>
            </Col>
          </>
        )}
      </div>
    </div>
  );
};

export default ResourceForm;
